import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  addcrmToStoreAPI,
  deletecrmAPI,
  fetchcrmAPI,
  updateDatecrmAPI,
  updateStatuscrmAPI,
} from "../../utils/api/crm";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import CRMForm from "./CRMForm/CRMForm";
import CRMExpand from "./CRMExpand/CRMExpand";
import { translations } from "../../utils/lang";
import {
  Bookmark,
  Button,
  Calendar,
  Checkbox,
  CircleX,
  Plus,
  Repeat,
  Table2,
  Upload,
  UserCheck,
  UserRoundPen,
  Input,
  Select,
  StatusDropdown,
  Search,
  Trash2,
  NoticeBox,
} from "../ui";
import BulkAssignBar from "./bulkAssignBar/BulkAssignBar";
import { ConfirmModal } from "../modals/Confirm";
import { DeleteModal } from "../modals/Delete";
import FilterGroup from "../analysis/filter/FilterUI";
import CRMSummary from "./summary/crmSummary";
import UploadCRMModal from "./uploadCRM/UploadCRM";
import CRMSchedule from "./schedule/CRMSchedule";
import "./CRM.css";

const STATUS_ENUM = [
  "new",
  "in_progress",
  "potential",
  "trial_active",
  "quote_sent",
  "active",
  "inactive",
];

const FOLLOWUP_ENUM = ["asap", "urgent", "date", "waiting"];
const FOLLOWUP_PRIORITY = {
  urgent: 1,
  asap: 2,
  date: 3,
  waiting: 4,
};

export default function CRMTable() {
  const [viewMode, setViewMode] = useState("table");
  const { handleTokenExpiration, lang } = useContext(Context);
  const { can } = useAccess();
  const [data, setData] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("create"); // or "edit"
  const [selectedCRM, setSelectedCRM] = useState(null);

  const [filters, setFilters] = useState({
    followUpFrom: null,
    followUpTo: null,
    assignedUsers: [],
    status: [],
    cities: [],
    packages: [],
    branchesNum: "",
    businessName: "",
    contactName: "",
    contactPhone: "",
  });

  const [editingDateId, setEditingDateId] = useState(null);

  const [tempDate, setTempDate] = useState("");
  const [tempDateStatus, setTempDateStatus] = useState("asap");

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);

  const [confirmData, setConfirmData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const assignedUsers = useMemo(() => {
    const map = new Map();

    data.forEach((item) => {
      const user = item.crm?.assignedUser;
      if (user && !map.has(user._id)) {
        map.set(user._id, user);
      }
    });

    return Array.from(map.values());
  }, [data]);
  const packages = useMemo(() => {
    return [
      ...new Set(
        data
          .map((item) => item.onboarding?.quotation?.subscriptionPackage)
          .filter(Boolean),
      ),
    ];
  }, [data]);
  const cities = useMemo(() => {
    return [
      ...new Set(
        data.map((item) => item.onboarding?.store?.city).filter(Boolean),
      ),
    ];
  }, [data]);
  const statuses = useMemo(() => {
    return [...new Set(data.map((item) => item.crm?.status).filter(Boolean))];
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const date = item.crm?.followUpEndDate
        ? new Date(item.crm.followUpEndDate)
        : null;

      const assignedUserId = item.crm?.assignedUser?.name;
      const status = item.crm?.status;
      const city = item.onboarding?.store?.city;
      const branchNumber = item.onboarding?.store?.branchCount;
      const pkg = item.onboarding?.quotation?.subscriptionPackage;

      // 1. Assigned users
      if (
        filters.assignedUsers.length > 0 &&
        !filters.assignedUsers.includes(assignedUserId)
      ) {
        return false;
      }

      // 2. Status
      if (filters.status.length > 0 && !filters.status.includes(status)) {
        return false;
      }

      // 3. City
      if (filters.cities.length > 0 && !filters.cities.includes(city)) {
        return false;
      }

      // 4. Package
      if (filters.packages.length > 0 && !filters.packages.includes(pkg)) {
        return false;
      }

      // 5. Branch number range
      if (filters.branchesNum && branchNumber !== Number(filters.branchesNum)) {
        return false;
      }

      // 6. Date
      if (filters.followUpFrom && (!date || date < filters.followUpFrom)) {
        return false;
      }

      if (filters.followUpTo && (!date || date > filters.followUpTo)) {
        return false;
      }

      if (
        filters.businessName &&
        !item.onboarding?.store?.businessName
          ?.toLowerCase()
          .includes(filters.businessName.toLowerCase())
      ) {
        return false;
      }

      if (
        filters.contactName &&
        !item.onboarding?.contact?.contactName
          ?.toLowerCase()
          .includes(filters.contactName.toLowerCase())
      ) {
        return false;
      }

      if (
        filters.contactPhone &&
        !item.onboarding?.contact?.mobile?.includes(filters.contactPhone)
      ) {
        return false;
      }

      return true;
    });
  }, [data, filters]);
  // duplicate detection for rows
  const duplicateMap = useMemo(() => {
    const businessCount = new Map();
    const phoneCount = new Map();

    data.forEach((item) => {
      const businessName = item.onboarding?.store?.businessName
        ?.trim()
        .toLowerCase();
      const phone = item.onboarding?.contact?.mobile?.trim();

      if (businessName) {
        businessCount.set(
          businessName,
          (businessCount.get(businessName) || 0) + 1,
        );
      }

      if (phone) {
        phoneCount.set(phone, (phoneCount.get(phone) || 0) + 1);
      }
    });

    return { businessCount, phoneCount };
  }, [data]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aStatus = a.crm?.dateStatus || "asap";
      const bStatus = b.crm?.dateStatus || "asap";

      const aPriority = FOLLOWUP_PRIORITY[aStatus];
      const bPriority = FOLLOWUP_PRIORITY[bStatus];

      // 1. Sort by priority first
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // 2. Then sort by date IF either has a date
      const aDate = a.crm?.followUpEndDate
        ? new Date(a.crm.followUpEndDate)
        : null;

      const bDate = b.crm?.followUpEndDate
        ? new Date(b.crm.followUpEndDate)
        : null;

      // If both have dates → sort by date
      if (aDate && bDate) {
        return aDate - bDate;
      }

      // If only one has a date → prioritize the one with a date
      if (aDate && !bDate) return -1;
      if (!aDate && bDate) return 1;

      return 0;
    });
  }, [filteredData]);
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timeout = setTimeout(() => setSuccessMessage(""), 3000);
    return () => clearTimeout(timeout);
  }, [successMessage]);

  const fetchData = async () => {
    const token = localStorage.getItem("auth-token");
    if (token) {
      setLoading(true);
      setLoadError("");
      try {
        const res = await fetchcrmAPI(token);
        setData(Array.isArray(res) ? res : []);
      } catch (error) {
        setLoadError(error?.message || "Unable to load CRM records");
      } finally {
        setLoading(false);
      }
    } else {
      handleTokenExpiration();
      setLoading(false);
    }
  };
  const deleteCRM = async (id) => {
    if (!can("crm.delete")) return;
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deletecrmAPI(token, id);
      setData((current) => current.filter((item) => item._id !== id));
      setSelectedIds((current) => current.filter((itemId) => itemId !== id));
      if (expandedId === id) setExpandedId(null);
      setDeleteTarget(null);
      setSuccessMessage(
        lang === "ar"
          ? "تم حذف سجل CRM بنجاح"
          : "CRM record deleted successfully",
      );
    } catch (error) {
      setDeleteError(
        error.message ||
          (lang === "ar" ? "تعذر الحذف" : "Unable to delete CRM record"),
      );
    } finally {
      setDeleteLoading(false);
    }
  };
  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) return handleTokenExpiration();

      await updateStatuscrmAPI(token, id, { status });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const updateDate = async (id) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) return handleTokenExpiration();

      await updateDatecrmAPI(token, id, {
        followUpEndDate: tempDate,
        dateStatus: tempDateStatus,
      });

      fetchData();
      setEditingDateId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const isToday = (date) => {
    const d = new Date(date);
    const now = new Date();

    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  };

  return (
    <main
      className="menu-container crm-page"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <header className="crm-page-header">
        <div className="crm-view-switch">
          <Button
            type="button"
            variant="primary"
            appearance={showForm ? "outline" : "soft"}
            size="sm"
            rounded
            icon={showForm ? <CircleX size={18} /> : <Plus size={18} />}
            onClick={() => {
              if (showForm) {
                setShowForm(false);
                return;
              }
              setMode("create");
              setSelectedCRM(null);
              setShowForm(true);
            }}
            aria-label={
              showForm
                ? lang === "ar"
                  ? "إغلاق النموذج"
                  : "Close form"
                : lang === "ar"
                  ? "إضافة عميل"
                  : "Add customer"
            }
          />
          <Button
            type="button"
            title="Table View"
            variant="primary"
            appearance={viewMode === "table" ? "filled" : "soft"}
            size="sm"
            rounded
            icon={<Table2 size={18} />}
            onClick={() => setViewMode("table")}
            aria-label={lang === "ar" ? "عرض الجدول" : "Table view"}
          />
          <Button
            type="button"
            title="Calendar View"
            variant="primary"
            appearance={viewMode === "schedule" ? "filled" : "soft"}
            size="sm"
            rounded
            icon={<Calendar size={18} />}
            onClick={() => setViewMode("schedule")}
            aria-label={lang === "ar" ? "عرض التقويم" : "Calendar view"}
          />
        </div>
        {can("crm.viewSwitch.use") && (
          <div className="crm-view-switch">
            <Button
              type="button"
              variant="primary"
              appearance="soft"
              size="sm"
              rounded
              title="Upload Excel"
              icon={<Upload size={18} />}
              onClick={() => setShowUploadModal(true)}
            />
            <Button
              type="button"
              variant="primary"
              appearance={bulkMode ? "filled" : "soft"}
              size="sm"
              rounded
              title={lang === "ar" ? "تعيين مجموعة" : "Bulk Assign"}
              icon={<UserRoundPen size={18} />}
              onClick={() => setBulkMode((prev) => !prev)}
            />
          </div>
        )}
      </header>

      {viewMode === "schedule" ? (
        <CRMSchedule data={data} lang={lang} />
      ) : (
        <>
          {showUploadModal && (
            <UploadCRMModal
              onClose={() => setShowUploadModal(false)}
              onUploaded={() => {
                setShowUploadModal(false);
                fetchData(); // refresh CRM
              }}
            />
          )}

          {bulkMode && (
            <BulkAssignBar
              selectedIds={selectedIds}
              onDone={() => {
                setBulkMode(false);
                setSelectedIds([]);
                fetchData();
              }}
            />
          )}
          {showForm && (
            <CRMForm
              mode={mode}
              initialData={selectedCRM}
              onClose={() => setShowForm(false)}
              onRefresh={() => {
                setShowForm(false);
                fetchData();
              }}
            />
          )}

          <section
            aria-label={lang === "ar" ? "فلاتر العملاء" : "Customer filters"}
          >
            <div className="filters">
              <Input
                type="date"
                label={lang === "ar" ? "من" : "From"}
                value={
                  filters.followUpFrom
                    ? filters.followUpFrom.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    followUpFrom: e.target.value
                      ? new Date(e.target.value)
                      : null,
                  }))
                }
                fullWidth={false}
              />

              <Input
                type="date"
                label={lang === "ar" ? "إلى" : "To"}
                value={
                  filters.followUpTo
                    ? filters.followUpTo.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    followUpTo: e.target.value
                      ? new Date(e.target.value)
                      : null,
                  }))
                }
                fullWidth={false}
              />

              <Input
                type="number"
                label={lang === "ar" ? "عدد الفروع" : "Branches Number"}
                value={filters.branchesNum}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    branchesNum: e.target.value,
                  }))
                }
                fullWidth={false}
              />

              <Input
                type="text"
                label={lang === "ar" ? "اسم المتجر" : "Store Name"}
                iconStart={<Search size={17} />}
                value={filters.businessName}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    businessName: e.target.value,
                  }))
                }
                fullWidth={false}
              />

              <Input
                type="text"
                label={lang === "ar" ? "اسم جهة الاتصال" : "Contact Name"}
                value={filters.contactName}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    contactName: e.target.value,
                  }))
                }
                fullWidth={false}
              />

              <Input
                type="text"
                label={lang === "ar" ? "رقم الجوال" : "Phone"}
                value={filters.contactPhone}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    contactPhone: e.target.value,
                  }))
                }
                fullWidth={false}
              />

              <FilterGroup
                label={lang === "ar" ? "الموظفين المسؤولين" : "Assigned Users"}
                options={assignedUsers.map((u) => u.name)}
                selected={filters.assignedUsers}
                onChange={(selectedList) =>
                  setFilters((prev) => ({
                    ...prev,
                    assignedUsers: selectedList,
                  }))
                }
              />

              <FilterGroup
                label={lang === "ar" ? "الحالة" : "Status"}
                options={statuses}
                selected={filters.status}
                onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
              />

              <FilterGroup
                label={lang === "ar" ? "المدينة" : "City"}
                options={cities}
                selected={filters.cities}
                onChange={(v) => setFilters((prev) => ({ ...prev, cities: v }))}
              />

              <FilterGroup
                label={lang === "ar" ? "الباقة" : "Package"}
                options={packages}
                selected={filters.packages}
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, packages: v }))
                }
              />

              <Button
                variant="danger"
                appearance="soft"
                size="sm"
                className="clear-filters-btn"
                onClick={() =>
                  setFilters({
                    followUpFrom: null,
                    followUpTo: null,
                    assignedUsers: [],
                    status: [],
                    cities: [],
                    packages: [],
                    branchesNum: "",
                    businessName: "",
                    contactName: "",
                    contactPhone: "",
                  })
                }
              >
                {lang === "ar" ? "مسح الفلاتر" : "Clear Filters"}
              </Button>
            </div>
          </section>

          <CRMSummary filteredData={filteredData} lang={lang} />
          {successMessage && (
            <NoticeBox variant="success" size="sm" className="crm-delete-toast">
              {successMessage}
            </NoticeBox>
          )}
          <section
            className="crm-table-card"
            aria-label={lang === "ar" ? "قائمة العملاء" : "Customer list"}
          >
            <div className="crm-table-card-header">
              <div>
                <h2>{lang === "ar" ? "العملاء" : "Customers"}</h2>
                <p>
                  {lang === "ar"
                    ? "انقر على أي صف لعرض التفاصيل"
                    : "Select a row to view its full details."}
                </p>
              </div>
              <span className="crm-result-count">{sortedData.length}</span>
            </div>
            {loadError ? (
              <div className="crm-state crm-state-error" role="alert">
                <CircleX size={28} />
                <strong>
                  {lang === "ar"
                    ? "تعذر تحميل البيانات"
                    : "CRM data could not be loaded"}
                </strong>
                <span>{loadError}</span>
                <Button size="sm" appearance="soft" onClick={fetchData}>
                  {lang === "ar" ? "إعادة المحاولة" : "Try again"}
                </Button>
              </div>
            ) : loading ? (
              <div
                className="crm-table-skeleton"
                aria-busy="true"
                aria-label={lang === "ar" ? "جار التحميل" : "Loading customers"}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <span key={index} />
                ))}
              </div>
            ) : sortedData.length === 0 ? (
              <div className="crm-state">
                <Search size={30} />
                <strong>
                  {lang === "ar" ? "لا توجد نتائج" : "No customers found"}
                </strong>
                <span>
                  {lang === "ar"
                    ? "جرّب تعديل عوامل التصفية أو إضافة عميل جديد"
                    : "Adjust your filters or add a new customer."}
                </span>
              </div>
            ) : (
              <div className="menu-table-scroll">
                <table className="menu-table">
                  <thead>
                    <tr>
                      {bulkMode && <th className="header"></th>}
                      {translations[lang].crm.headers.map((item, i) => {
                        return (
                          <th className="header" key={i}>
                            {item}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {sortedData.map((crm) => {
                      const isExpanded = expandedId === crm._id;
                      const status = crm.crm?.dateStatus || "asap";
                      const date = crm.crm?.followUpEndDate;
                      const businessName = crm.onboarding?.store?.businessName
                        ?.trim()
                        .toLowerCase();
                      const phone = crm.onboarding?.contact?.mobile?.trim();

                      const duplicate =
                        duplicateMap.businessCount.get(businessName) > 1 ||
                        duplicateMap.phoneCount.get(phone) > 1;

                      const dateClass =
                        status === "date"
                          ? isToday(date)
                            ? "today"
                            : "other"
                          : "";

                      const titleText =
                        translations[lang].crm.dateStatus[status];

                      return (
                        <React.Fragment key={crm._id}>
                          {/* MAIN ROW */}
                          <tr
                            className={`menu-row 
    ${isExpanded ? "expanded" : ""} 
    ${duplicate ? "duplicate-row" : ""}
  `}
                            onClick={() =>
                              setExpandedId(isExpanded ? null : crm._id)
                            }
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setExpandedId(isExpanded ? null : crm._id);
                              }
                            }}
                          >
                            {bulkMode && (
                              <td onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedIds.includes(crm._id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedIds((prev) => [
                                        ...prev,
                                        crm._id,
                                      ]);
                                    } else {
                                      setSelectedIds((prev) =>
                                        prev.filter((id) => id !== crm._id),
                                      );
                                    }
                                  }}
                                />
                              </td>
                            )}
                            <td>
                              {crm?.isSelfClient && (
                                <UserCheck
                                  size={16}
                                  className="crm-self-client-icon"
                                />
                              )}
                              {crm.onboarding?.store?.businessName}
                            </td>
                            <td>{crm.onboarding?.store?.branchCount}</td>
                            <td>{crm.onboarding?.store?.city}</td>

                            <td className="contact-cell">
                              <div>{crm.onboarding?.contact?.contactName}</div>
                              <div className="muted">
                                {crm.onboarding?.contact?.email}
                              </div>
                              <div className="muted">
                                {crm.onboarding?.contact?.mobile}
                              </div>
                            </td>

                            <td className="note-cell">
                              <div>
                                {crm.onboarding?.documents?.notes || "-"}
                              </div>
                            </td>
                            <td className="restaurants-list-table-p">
                              <StatusDropdown
                                value={crm.crm?.status || "active"}
                                options={STATUS_ENUM}
                                editable
                                getLabel={(status) =>
                                  translations[lang].restaurants.restaurantsRow
                                    .status[status]
                                }
                                onChange={(status) =>
                                  updateStatus(crm._id, status)
                                }
                              />
                            </td>
                            <td>
                              <div
                                className={`flag ${status} ${dateClass}`}
                                title={titleText}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDateId(crm._id);
                                  setTempDate(date || "");
                                  setTempDateStatus(status);
                                }}
                              >
                                <span className="flag-indicator">
                                  <Bookmark />
                                </span>
                                <p>
                                  {date ? new Date(date).toLocaleString() : "-"}
                                </p>
                              </div>
                              {can("crm.assignee.view") && (
                                <div>{crm.crm?.assignedUser?.name}</div>
                              )}

                              {editingDateId === crm._id && (
                                <div
                                  className="modal-overlay"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingDateId(null);
                                  }} // click outside = close
                                >
                                  <div
                                    className="modal-content"
                                    onClick={(e) => e.stopPropagation()} // prevent closing inside
                                  >
                                    <Select
                                      label={
                                        lang === "ar"
                                          ? "حالة التاريخ"
                                          : "Date Status"
                                      }
                                      value={tempDateStatus}
                                      options={FOLLOWUP_ENUM.map((s) => ({
                                        value: s,
                                        label:
                                          translations[lang].crm.dateStatus[
                                            s || "date"
                                          ],
                                      }))}
                                      onChange={(e) =>
                                        setTempDateStatus(e.target.value)
                                      }
                                    />

                                    <Input
                                      label={
                                        lang === "ar"
                                          ? "تاريخ المتابعة"
                                          : "Follow Up Date"
                                      }
                                      type="datetime-local"
                                      value={tempDate}
                                      onChange={(e) =>
                                        setTempDate(e.target.value)
                                      }
                                    />

                                    <div className="confirm-btn">
                                      <Button
                                        type="button"
                                        variant="primary"
                                        appearance="filled"
                                        size="sm"
                                        onClick={() => updateDate(crm._id)}
                                      >
                                        {lang === "ar" ? "حفظ" : "Save"}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>

                            <td>
                              <Button
                                type="button"
                                variant="primary"
                                appearance="soft"
                                size="sm"
                                rounded
                                // className="menu-action-btn update"
                                icon={<Repeat size={17} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMode("edit");
                                  setSelectedCRM(crm);
                                  setShowForm(true);
                                }}
                                aria-label={
                                  lang === "ar"
                                    ? "تعديل العميل"
                                    : "Edit customer"
                                }
                              />
                              {can("crm.restaurant.convert") && (
                                <>
                                  {crm.crm.isAddedStore ? (
                                    <Button
                                      type="button"
                                      variant="success"
                                      appearance="soft"
                                      size="sm"
                                      rounded
                                      // className="menu-action-btn update"
                                      icon={<Plus size={17} />}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <Button
                                      type="button"
                                      variant="danger"
                                      appearance="soft"
                                      size="sm"
                                      rounded
                                      // className="menu-action-btn update"
                                      icon={<Plus size={17} />}
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        setConfirmData({
                                          crmId: crm._id,
                                          name: crm.onboarding?.store
                                            ?.businessName,
                                        });
                                      }}
                                    />
                                  )}
                                </>
                              )}
                              
                              {can("crm.delete") && (
                                <Button
                                  type="button"
                                  variant="danger"
                                  appearance="soft"
                                  size="sm"
                                  rounded
                                  icon={<Trash2 size={17} />}
                                  aria-label={
                                    lang === "ar"
                                      ? "حذف سجل CRM"
                                      : "Delete CRM record"
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setDeleteError("");
                                    setDeleteTarget(crm);
                                  }}
                                />
                              )}
                            </td>
                          </tr>

                          {/* EXPANDED ROW */}
                          {isExpanded && (
                            <tr className="expanded-row">
                              <td colSpan={bulkMode ? 9 : 8}>
                                <CRMExpand crm={crm} onRefresh={fetchData} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {confirmData && (
            <ConfirmModal
              message={`Create store for ${confirmData.name}?`}
              onConfirm={async () => {
                try {
                  // Handler-level check (NA-010): the dialog is only a prompt. The
                  // record must be one this page loaded and not already converted.
                  // Converting creates a new restaurant, so no existing restaurant
                  // scope applies; role access is the check.
                  const record = data.find((item) => item._id === confirmData.crmId);
                  if (!can("crm.restaurant.convert") || !record || record.crm?.isAddedStore) {
                    setConfirmData(null);
                    return;
                  }
                  const token = localStorage.getItem("auth-token");
                  if (!token) return handleTokenExpiration();

                  await addcrmToStoreAPI(token, confirmData.crmId);

                  setConfirmData(null);
                  fetchData();
                } catch (err) {
                  console.error(err);
                }
              }}
              onCancel={() => setConfirmData(null)}
            />
          )}
          {can("crm.delete") && deleteTarget && (
            <DeleteModal
              id={deleteTarget._id}
              loading={deleteLoading}
              error={deleteError}
              description={
                lang === "ar"
                  ? `سيتم حذف سجل CRM الخاص بـ "${deleteTarget.onboarding?.store?.businessName || "بدون اسم"}". سيبقى طلب التهيئة محفوظاً.`
                  : `This will delete the CRM record for "${deleteTarget.onboarding?.store?.businessName || "Unnamed"}". The onboarding record will be preserved.`
              }
              handleDelete={deleteCRM}
              cancel={() => {
                if (!deleteLoading) {
                  setDeleteTarget(null);
                  setDeleteError("");
                }
              }}
            />
          )}
        </>
      )}
    </main>
  );
}
